import { eventTracker } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { MicrophoneIcon, StopIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useEffect, useMemo } from 'react';
import { useMicRecorder } from './useMicRecorder';

interface Props {
  onClose: () => void;
  onSubmitAudio: (file: File) => void;
}

const ROW = 'flex flex-wrap items-center justify-center gap-4';

export function Recorder({ onClose, onSubmitAudio }: Props) {
  const { state, stop, restart } = useMicRecorder(onClose);
  const file = state.kind === 'stopped' ? state.file : null;
  const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    if (!url) return;
    return () => URL.revokeObjectURL(url);
  }, [url]);

  const status = (label: string, ping?: boolean) => (
    <div className="flex items-center justify-center">
      <div className="flex items-center gap-2">
        {ping && (
          <span className="inline-flex h-3 w-3">
            <span className="absolute inline-flex h-3 w-3 animate-ping rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500"></span>
          </span>
        )}
        <span className="dark:text-dark-secondary-text text-gray-500">
          {label}
        </span>
      </div>
    </div>
  );

  switch (state.kind) {
    case 'acquiring':
      return <div className={ROW}>{status('Starting microphone...')}</div>;
    case 'recording':
      return (
        <div className={ROW}>
          {status('Recording audio...', true)}
          <Button variant="icon" onClick={stop}>
            <StopIcon className="w-5" />
          </Button>
        </div>
      );
    case 'stopped':
      return (
        <div className={ROW}>
          <div className="flex items-center justify-center">
            <span className="-mr-10">
              <Button variant="icon" onClick={onClose}>
                <TrashIcon className="w-5" />
              </Button>
            </span>
            <audio
              src={url ?? undefined}
              className="inline-block h-12 scale-75"
              controls
            />
            <span className="-ml-10">
              <Button variant="icon" onClick={restart}>
                <MicrophoneIcon className="w-5 text-red-500" />
              </Button>
            </span>
          </div>
          <Button
            variant="primary"
            onClick={() => {
              onSubmitAudio(state.file);
              onClose();
            }}
            onSideEffect={eventTracker('submit_manual_camera_audio_talkdown')}
          >
            Send audio
          </Button>
        </div>
      );
  }
}

export default Recorder;
